import React, { PureComponent } from 'react'
import BEMHelper from '@ynap/bem-helper'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import Field, { InputGroup, Input } from '@ynap/field'
import { CombinedSelectWithCustomValue } from '@ynap/select'
import { SimpleAsyncButton } from '@ynap/button'
import Notification from '@ynap/notification'
import track from 'react-tracking'
import { trackEditPersonalInformation } from '../analytics'
import validate from './validate'
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils'
import collateErrors from './collateErrors'

const bem = new BEMHelper('PersonalInfoForm')

const propTypes = {
    messages: PropTypes.shape({
        personalInfoForm: PropTypes.shape({
            personTitle: PropTypes.func.isRequired,
            firstName: PropTypes.func.isRequired,
            lastName: PropTypes.func.isRequired,
            submit: PropTypes.func.isRequired,
            dayPlaceholder: PropTypes.func.isRequired,
            monthPlaceholder: PropTypes.func.isRequired,
            yearPlaceholder: PropTypes.func.isRequired,
            selectTitle: PropTypes.func.isRequired,
            other: PropTypes.func.isRequired,
            dobHelp: PropTypes.func.isRequired,
        }),
        errors: PropTypes.shape({
            required: PropTypes.func.isRequired,
            generic: PropTypes.func.isRequired,
            global: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    locale: PropTypes.shape({
        language: PropTypes.string,
    }),
    form: PropTypes.string,
    className: PropTypes.string,
    onSubmit: PropTypes.func,
    handleSubmit: PropTypes.func,
    error: PropTypes.string,
    titles: PropTypes.array.isRequired,
    enableTitles: PropTypes.bool,
    enableAgeConsent: PropTypes.bool,
}

@track()
export class PersonalInfoForm extends PureComponent {
    constructor(props) {
        super(props)
        this.onSubmit = this.onSubmit.bind(this)
    }

    @track(trackEditPersonalInformation())
    onSubmit(values) {
        const { form, onSubmit } = this.props
        return submitAndCollateErrorsPromise(onSubmit(values, form), values, this.props)
    }

    render() {
        const { messages, form, className, children, handleSubmit, error, enableTitles, submitting, locale } =
            this.props
        const { language } = locale

        const formatDateArrangement = () => {
            Array.prototype.doNothing = function () {
                return this
            }

            const dates = [
                {
                    dateType: 'day',
                    message: messages?.personalInfoForm.dayPlaceholder(),
                },
                {
                    dateType: 'month',
                    message: messages?.personalInfoForm.monthPlaceholder(),
                },
            ]

            return dates[locale.country === 'US' ? 'reverse' : 'doNothing']().map((el) => (
                <Input className={bem('dateOfBirthItem')} name={el.dateType} maxLength="2" placeholder={el.message} />
            ))
        }

        const titleOptions = Object.keys(messages.titles).reduce((acc, title) => {
            const titleName = messages.titles[title]()
            if (titleName === '') return acc

            if (titleName === messages.titles.other()) {
                acc.push({
                    key: '_custom',
                    value: '_custom',
                    label: messages.titles.other(),
                    labelNative: messages.titles.other(),
                })
            } else {
                acc.push({
                    key: titleName,
                    value: titleName,
                    label: titleName,
                    labelNative: titleName,
                })
            }
            return acc
        }, [])

        return (
            <form id={form} className={bem(null, null, className)} onSubmit={handleSubmit(this.onSubmit)}>
                {enableTitles ? (
                    <Field
                        component={CombinedSelectWithCustomValue}
                        className={bem('title')}
                        name="personTitle"
                        label={messages.personalInfoForm.personTitle()}
                        emptyLabel={messages.personalInfoForm.selectTitle()}
                        autoComplete="honorific-prefix"
                        options={[...titleOptions]}
                    />
                ) : null}
                <Field
                    name="firstName"
                    label={messages.personalInfoForm.firstName()}
                    className={bem('firstName', language)}
                />
                <Field
                    name="lastName"
                    label={messages.personalInfoForm.lastName()}
                    className={bem('lastName', language)}
                />
                <Field
                    className={bem('dateOfBirthGroup', language)}
                    component={InputGroup}
                    name="dateOfBirthGroup"
                    label={messages.personalInfoForm.dob()}
                    help={
                        locale.country === 'US'
                            ? messages.personalInfoForm?.dobHelpUS()
                            : messages.personalInfoForm?.dobHelp()
                    }
                >
                    {formatDateArrangement()}
                    <Input
                        className={bem('dateOfBirthItem')}
                        name="year"
                        maxLength="4"
                        placeholder={messages.personalInfoForm.yearPlaceholder()}
                    />
                </Field>

                <div className={bem('buttonWrapper', language)}>
                    <SimpleAsyncButton
                        className={bem('submit', language)}
                        copy={messages.personalInfoForm.submit()}
                        loading={submitting}
                    />
                </div>

                {error ? <Notification type="error" message={error} className={bem('error')} /> : null}

                {children}
            </form>
        )
    }
}

PersonalInfoForm.propTypes = propTypes

const PersonalInfoFormContainer = connect((state) => {
    return {
        enableTitles: state.config ? state.config.head.flags.enableTitles : true,
    }
})(PersonalInfoForm)

export default reduxForm({ form: 'personalInfo', validate, collateErrors, onSubmitFail })(PersonalInfoFormContainer)
